import React, { useState, useEffect, useRef, useReducer } from "react";
import axios from "axios";
import { toast } from "react-toastify";

//components
import { Button, Columns, Form } from 'react-bulma-components';
import { AddContactPanel, ContactDetailPanel } from "shared/components/user";
import ContactsTable from "./ContactsTable";
import Loading from "shared/components/loading/Loading";
import { Download, Info, Upload, X } from 'react-feather';

// actions
import { makeAPIRequest } from "shared/state/fetch";
import { useTokenStatus } from "shared/state/auth/actions";

// utils
import ContactBg from 'shared/media/contact-list-bg.svg';

// Styles
import styles from './Contacts.module.scss';

const API_URL = process.env.REACT_APP_API_URL;

const Contacts = () => {
    const { Input, InputFile, Field, Control, Label } = Form;
    const [isLoading, setIsLoading] = useState(true);
    const [contactsList, setContactsList] = useState([]);
    const [shouldFetch, setShouldFetch] = useState(true);
    const [isDetailActive, setIsDetailActive] = useState(false);
    const [selectedContact, setSelectedContact] = useState('');
    const [addNewContact, setAddNewContact] = useState(false);
    const [importIsVisible, setImportIsVisible] = useState(false);
    const [importFile, setImportFile] = useState('');
    const { handleTokenStatus } = useTokenStatus();
    const isMounted = useRef(null);

    useEffect(() => {
        isMounted.current = true;

        if(shouldFetch){
            makeAPIRequest('contacts?perPage=100')
                .then(response => {
                    if (isMounted.current) {
                        setShouldFetch(false);
                        setIsLoading(false);
                        setContactsList(response.result.data);
                        response.status && handleTokenStatus(response);
                    }
                }
            );
        }
        return () => {
            // executed when unmount
            isMounted.current = false;
        }
    }, [shouldFetch]);

    const handleSelectedContact = (contact) => {
        setIsDetailActive(true);
        setSelectedContact(contact);
    }

    /*
        TODO: Add a message asking to confirm delete. Apply a timer to revert
        the action, only after the timer ends should the action be executed
        -> use toast for this feature or add undo directly on row
    */
    const handleRemoveContact = (contact) => {
        makeAPIRequest(`contacts/${contact.id}`, 'DELETE', contact)
            .then(response => {
                setShouldFetch(true);
                handleClose();
                response.status && handleTokenStatus(response);
            })
    }

    const handleOnAddNewContact = (newContact) => {
        makeAPIRequest('contacts', 'POST', newContact)
            .then(response => {
                if (response.result.data && !response.status){
                    handleClose();
                    setContactsList([...contactsList, response.result.data]);
                }
                //response.status && handleTokenStatus(response);
            }
        )
    }

    const handleAfterSaveContact = ({ shouldFetch }) => {
        setShouldFetch(shouldFetch);
    }

    const handleClose = () => {
        setIsDetailActive(false);
        setSelectedContact(false);
        setAddNewContact(false);
    }

    const onClickAddContact = () => {
        setAddNewContact(true);
    }

    const handleClickImport = (e) => {
        e.preventDefault();
        setImportIsVisible(!importIsVisible);
    }

    const handleFileUpload = (e) => {
        if (e.target.files.length > 0) {
            setImportFile(e.target.files[0]);
        }
    }

    const handleImportContacts = (e) => {
        e.preventDefault();

        const bodyFormData = new FormData(e.target);

        const headers = {
            'Access-Control-Allow-Origin': '*',
            'Accept': 'application/json',
            'Content-Type': 'multipart/form-data'
        }

        makeAPIRequest('contacts/upload-excel', 'POST', bodyFormData, headers)
            .then(response => {
                console.log(response);
                setShouldFetch(true);
                setImportIsVisible(false);
                setImportFile('');
            }
        )
    }

    const handleDownloadSample = (e) => {
        e.preventDefault();

        axios({
            method: 'POST',
            url: API_URL + 'contacts/download-excel-sample',
            data: null,
            withCredentials: true,
            headers:
            {
                'Content-Disposition': "attachment; filename=template.xlsx",
                'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            },
            responseType: 'arraybuffer',
        })
        .then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'contacts-sample.xlsx');
            document.body.appendChild(link);
            link.click();
            window.URL.revokeObjectURL(url);
            link.remove();
        })
        .catch((error) => console.log(error));
    }

    return (
        <>
            { !isLoading &&
                <AddContactPanel
                    isActive={ addNewContact }
                    onClose={ handleClose }
                    onAddNewContact={ handleOnAddNewContact } />
            }

            { // Wait for fetch beforing showing components depending on that data
                !isLoading &&
                    <ContactDetailPanel
                        isActive={ isDetailActive }
                        onClose={ handleClose }
                        contact={ selectedContact }
                        afterSaveContact={ handleAfterSaveContact } />
            }

            <div className={ styles.contacts }>
                <div className="pageHeading">
                    <h1>Contact List</h1>
                    <div className={ styles.actions }>
                        <Button size="small" className="gradient" onClick={ onClickAddContact }>
                            New Contact
                        </Button>

                        <Button.Group>
                            <Button size="small"
                                color={ importIsVisible ? 'dark' : 'text'}
                                onClick={ handleClickImport }
                                rounded>
                                Import { importIsVisible ? <X size={15} /> : <Upload size={15} />}
                            </Button>
                            <Button size="small" color="text" rounded disabled>
                                Export
                                <Download size={15} />
                            </Button>
                        </Button.Group>
                    </div>
                </div>
                <div className={ `${ styles.importRow} ${importIsVisible ? styles.active : ''}` }>
                    <div className={ styles.row }>
                            <form onSubmit={ handleImportContacts }>
                                <p><strong>Have contacts you'd like to import?</strong></p>
                                <Field>
                                    <Control className={ styles.controller }>
                                        <InputFile
                                            className={ styles.upload }
                                            name="file"
                                            label={ importFile !=='' ? importFile.name : 'Upload your file here' }
                                            icon={<Upload size={16} />}
                                            onChange={ handleFileUpload } />
                                        <Button
                                            color="primary"
                                            disabled={ importFile !== '' ? false : true }
                                            type="submit"
                                            className={ styles.secondary }>
                                            Import
                                        </Button>
                                    </Control>

                                </Field>
                            </form>


                        <div className={ styles.download }>
                            <Info size={18} />
                            <Button size="small" text color="ghost" onClick={ handleDownloadSample }>
                                Download the sample Excel before importing
                            </Button>
                        </div>
                    </div>
                </div>

                { /* No contacts found */
                (!contactsList || contactsList.length === 0) && !isLoading &&
                    <Columns className="is-gapless">
                        <Columns.Column size={4} style={{ position: 'relative'}}>
                            <p className={ styles.noMessage }>No contacts to display.</p>
                        </Columns.Column>

                        <Columns.Column>
                            <div className="section" style={{
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                 <figure style={{ flex: 1, maxWidth: '90%'}}>
                                     <img src={ContactBg} alt="Illustration" role="presentation" />
                                 </figure>
                             </div>
                        </Columns.Column>
                    </Columns>
                }
                { contactsList && contactsList.length > 0 &&
                    <ContactsTable
                        data={ contactsList }
                        onSelect={ handleSelectedContact }
                        onRemove={ handleRemoveContact }
                    />
                }
            </div>
            <Loading isLoading={ isLoading && contactsList.length === 0 }/>
        </>
    );
}

export default Contacts;
